import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowLeft,
  Loader2,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { TimeSlotPicker } from '@/components/admin/agenda'
import { useAgendaConfig, useAvailability, useBookAppointment } from '@/hooks/useAgenda'
import type { AvailableSlot, AppointmentDuration } from '@/types/agenda'
import { appointmentDurationLabels } from '@/types/agenda'

/**
 * STORY-044: Page publique de prise de RDV
 *
 * Permet aux clients de réserver un créneau en ligne
 */
export default function BookingPage() {
  const { adminId } = useParams<{ adminId: string }>()

  // État du formulaire
  const [step, setStep] = useState<'selection' | 'form' | 'confirmation'>('selection')
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null)
  const [selectedDuration, setSelectedDuration] = useState<AppointmentDuration>('60')
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    subject: '',
    description: '',
  })
  const [bookingResult, setBookingResult] = useState<{
    success: boolean
    message?: string
    modificationToken?: string
  } | null>(null)

  // Hooks
  const { data: config, isLoading: isLoadingConfig } = useAgendaConfig(adminId || '')

  // Calculer les dates de disponibilité (4 semaines à l'avance)
  const { startDate, endDate } = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date(start)
    end.setDate(end.getDate() + 28)
    return { startDate: start, endDate: end }
  }, [])

  const { data: availability, isLoading: isLoadingAvailability } = useAvailability(
    adminId || '',
    startDate,
    endDate,
    parseInt(selectedDuration)
  )

  const bookAppointment = useBookAppointment()

  // Handlers
  const handleSlotSelect = (slot: AvailableSlot) => {
    setSelectedSlot(slot)
  }

  const handleContinueToForm = () => {
    if (!selectedSlot) return
    setStep('form')
  }

  const handleBackToSelection = () => {
    setStep('selection')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSlot || !adminId) return

    try {
      const result = await bookAppointment.mutateAsync({
        adminId,
        slotStart: selectedSlot.start.toISOString(),
        duration: parseInt(selectedDuration),
        clientName: formData.clientName,
        clientEmail: formData.clientEmail,
        clientPhone: formData.clientPhone || undefined,
        subject: formData.subject,
        description: formData.description || undefined,
      })

      setBookingResult({
        success: result.success,
        message: result.error,
        modificationToken: result.modificationToken,
      })
      setStep('confirmation')
    } catch (error) {
      console.error('Erreur réservation:', error)
      setBookingResult({
        success: false,
        message: 'Une erreur est survenue lors de la réservation.',
      })
      setStep('confirmation')
    }
  }

  const handleNewBooking = () => {
    setStep('selection')
    setSelectedSlot(null)
    setFormData({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      subject: '',
      description: '',
    })
    setBookingResult(null)
  }

  // Loading state
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-beige-50 flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  // Réservation désactivée
  if (!config?.is_booking_enabled) {
    return (
      <div className="min-h-screen bg-beige-50 flex items-center justify-center p-4">
        <Card padding="lg" className="max-w-md text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gold-500" />
          <h1 className="text-xl font-bold text-brown-900 mb-2">
            Réservation non disponible
          </h1>
          <p className="text-brown-600">
            La prise de rendez-vous en ligne n'est pas activée pour le moment.
            Veuillez nous contacter directement.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-beige-50">
      {/* Header */}
      <div className="bg-white border-b border-beige-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-brown-900 mb-2">
              Prendre rendez-vous
            </h1>
            {config.welcome_message && (
              <p className="text-brown-600">{config.welcome_message}</p>
            )}
          </div>

          {/* Stepper */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <StepIndicator
              step={1}
              label="Sélection"
              isActive={step === 'selection'}
              isCompleted={step === 'form' || step === 'confirmation'}
            />
            <div className="w-16 h-0.5 bg-beige-200">
              <div
                className={`h-full bg-mint-600 transition-all ${
                  step === 'form' || step === 'confirmation' ? 'w-full' : 'w-0'
                }`}
              />
            </div>
            <StepIndicator
              step={2}
              label="Informations"
              isActive={step === 'form'}
              isCompleted={step === 'confirmation'}
            />
            <div className="w-16 h-0.5 bg-beige-200">
              <div
                className={`h-full bg-mint-600 transition-all ${
                  step === 'confirmation' ? 'w-full' : 'w-0'
                }`}
              />
            </div>
            <StepIndicator
              step={3}
              label="Confirmation"
              isActive={step === 'confirmation'}
              isCompleted={false}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Étape 1 : Sélection du créneau */}
          {step === 'selection' && (
            <motion.div
              key="selection"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card padding="lg">
                {/* Sélection durée */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-brown-700 mb-2">
                    Durée du rendez-vous
                  </label>
                  <Select
                    value={selectedDuration}
                    onChange={(e) => {
                      setSelectedDuration(e.target.value as AppointmentDuration)
                      setSelectedSlot(null) // Reset slot selection when duration changes
                    }}
                  >
                    {Object.entries(appointmentDurationLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Sélecteur de créneaux */}
                <TimeSlotPicker
                  availability={availability || []}
                  selectedSlot={selectedSlot}
                  onSlotSelect={handleSlotSelect}
                  duration={parseInt(selectedDuration)}
                  isLoading={isLoadingAvailability}
                />

                {/* Bouton continuer */}
                {selectedSlot && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 flex justify-end"
                  >
                    <Button onClick={handleContinueToForm} size="lg">
                      Continuer
                    </Button>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          )}

          {/* Étape 2 : Formulaire */}
          {step === 'form' && selectedSlot && (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Card padding="lg">
                {/* Récapitulatif du créneau */}
                <div className="bg-mint-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-mint-100 rounded-lg">
                      <Calendar className="w-5 h-5 text-mint-600" />
                    </div>
                    <div>
                      <div className="font-medium text-brown-900">
                        {selectedSlot.start.toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </div>
                      <div className="text-brown-600">
                        {selectedSlot.start.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        - {selectedDuration} minutes
                      </div>
                    </div>
                  </div>
                </div>

                {/* Formulaire */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        <User className="w-4 h-4 inline-block mr-1" />
                        Nom complet *
                      </label>
                      <Input
                        value={formData.clientName}
                        onChange={(e) =>
                          setFormData({ ...formData, clientName: e.target.value })
                        }
                        placeholder="Jean Dupont"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-brown-700 mb-1">
                        <Mail className="w-4 h-4 inline-block mr-1" />
                        Email *
                      </label>
                      <Input
                        type="email"
                        value={formData.clientEmail}
                        onChange={(e) =>
                          setFormData({ ...formData, clientEmail: e.target.value })
                        }
                        placeholder="jean@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      <Phone className="w-4 h-4 inline-block mr-1" />
                      Téléphone
                    </label>
                    <Input
                      type="tel"
                      value={formData.clientPhone}
                      onChange={(e) =>
                        setFormData({ ...formData, clientPhone: e.target.value })
                      }
                      placeholder="06 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      <FileText className="w-4 h-4 inline-block mr-1" />
                      Objet du rendez-vous *
                    </label>
                    <Input
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      placeholder="Consultation initiale, projet web, etc."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      Description (optionnel)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({ ...formData, description: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                      rows={4}
                      placeholder="Décrivez brièvement la raison de votre rendez-vous..."
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleBackToSelection}
                      leftIcon={<ArrowLeft className="w-4 h-4" />}
                    >
                      Retour
                    </Button>
                    <Button
                      type="submit"
                      isLoading={bookAppointment.isPending}
                      size="lg"
                    >
                      Confirmer le rendez-vous
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          )}

          {/* Étape 3 : Confirmation */}
          {step === 'confirmation' && bookingResult && (
            <motion.div
              key="confirmation"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Card padding="lg" className="text-center">
                {bookingResult.success ? (
                  <>
                    <div className="w-20 h-20 mx-auto mb-6 bg-mint-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-12 h-12 text-mint-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-brown-900 mb-3">
                      Rendez-vous confirmé !
                    </h2>
                    <p className="text-brown-600 mb-6">
                      Votre rendez-vous a été enregistré avec succès. Vous allez recevoir
                      un email de confirmation à l'adresse indiquée.
                    </p>

                    {selectedSlot && (
                      <div className="bg-mint-50 rounded-lg p-6 mb-6">
                        <div className="flex items-center justify-center gap-4 mb-4">
                          <Calendar className="w-6 h-6 text-mint-600" />
                          <div className="text-left">
                            <div className="font-semibold text-brown-900">
                              {selectedSlot.start.toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </div>
                            <div className="text-brown-600">
                              {selectedSlot.start.toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-brown-600">
                          <Clock className="w-4 h-4 inline-block mr-1" />
                          Durée : {selectedDuration} minutes
                        </div>
                      </div>
                    )}

                    <div className="space-y-2 text-sm text-brown-600">
                      <p>
                        Un rappel vous sera envoyé 24h avant le rendez-vous et 1h avant.
                      </p>
                      <p>
                        Si vous devez modifier ou annuler ce rendez-vous, utilisez le lien
                        dans l'email de confirmation.
                      </p>
                    </div>

                    <Button onClick={handleNewBooking} variant="outline" className="mt-6">
                      Prendre un autre rendez-vous
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-12 h-12 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-brown-900 mb-3">
                      Erreur de réservation
                    </h2>
                    <p className="text-brown-600 mb-6">
                      {bookingResult.message ||
                        'Une erreur est survenue lors de la réservation. Veuillez réessayer.'}
                    </p>
                    <Button onClick={handleNewBooking}>Réessayer</Button>
                  </>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// Composant indicateur d'étape
interface StepIndicatorProps {
  step: number
  label: string
  isActive: boolean
  isCompleted: boolean
}

function StepIndicator({ step, label, isActive, isCompleted }: StepIndicatorProps) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`
          w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all
          ${
            isCompleted
              ? 'bg-mint-600 text-white'
              : isActive
              ? 'bg-mint-600 text-white ring-4 ring-mint-100'
              : 'bg-beige-200 text-brown-400'
          }
        `}
      >
        {isCompleted ? <CheckCircle className="w-5 h-5" /> : step}
      </div>
      <span
        className={`text-xs font-medium ${
          isActive ? 'text-brown-900' : 'text-brown-400'
        }`}
      >
        {label}
      </span>
    </div>
  )
}
